import styled, { css } from 'styled-components';
import readableTextOn from '../../../utils/readableTextOn';

interface CounterCardProps {
  color: string;
}

interface VariationProps {
  positive: boolean;
}

interface BadgeProps {
  color: string;
}

interface FullscreenProps {
  fullscreen: boolean;
}

export const Container = styled.div<FullscreenProps>`
  display: flex;
  flex-direction: column;
  gap: 24px;

  /*
    Modo telão, herdado do painel de contingência: a tela sai do template e ocupa o
    monitor inteiro. O fundo neutro separa os cartões brancos numa sala projetada.
  */
  ${({ fullscreen }) =>
    fullscreen &&
    css`
      position: fixed;
      inset: 0;
      z-index: 1000;
      overflow-y: auto;
      padding: 24px;
      background: #eef0f4;

      /*
        ⚠️ Com altura fixa (inset: 0), a coluna flex encolhe os filhos para caber na
        janela, e a tabela virava uma faixa de uma linha com rolagem própria (visto
        no navegador em 2026-09-25). Quem rola é o container, não a tabela.
      */
      > * {
        flex-shrink: 0;
      }
    `}
`;

export const Header = styled.header`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  flex-wrap: wrap;
`;

export const GhostButton = styled.button`
  background: #ffffff;
  border: 1px solid #d6d6e0;
  border-radius: 6px;
  color: #4f4f4f;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  padding: 8px 14px;
  transition: background 0.2s;

  &:hover {
    background: #f4f4f8;
  }
`;

export const TopBlocks = styled.div`
  display: grid;
  grid-template-columns: 3fr 2fr;
  gap: 24px;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

export const Card = styled.div`
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.05);
  padding: 20px 24px;
`;

export const CardTitle = styled.h2`
  font-size: 16px;
  font-weight: 600;
  color: #4f4f4f;
  margin-bottom: 4px;
`;

export const CardNote = styled.p`
  font-size: 12px;
  color: #828282;
  margin-bottom: 16px;
`;

export const Counters = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 12px;

  @media (max-width: 700px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

export const CounterCard = styled.div<CounterCardProps>`
  border-left: 4px solid ${({ color }) => color};
  background: #fafafa;
  border-radius: 4px;
  padding: 12px 14px;

  > span {
    display: block;
    font-size: 12px;
    color: #828282;
  }

  > strong {
    display: block;
    font-size: 28px;
    line-height: 34px;
    color: ${({ color }) => color};
  }
`;

export const Variation = styled.small<VariationProps>`
  font-size: 11px;
  color: ${({ positive }) => (positive ? '#EA7676' : '#6FCF97')};
`;

export const InactivityBands = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const Band = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  color: #4f4f4f;

  > span:first-child {
    min-width: 130px;
  }

  > b {
    min-width: 40px;
    text-align: right;
  }
`;

export const BandTrack = styled.div`
  flex: 1;
  height: 8px;
  border-radius: 4px;
  background: #f0f0f0;
  overflow: hidden;
`;

interface BandFillProps {
  percent: number;
  color: string;
}

export const BandFill = styled.div<BandFillProps>`
  height: 100%;
  width: ${({ percent }) => percent}%;
  background: ${({ color }) => color};
`;

export const Table = styled.div`
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.05);
  overflow-x: auto;
`;

/**
 * ⚠️ **As oito colunas da spec** (`02-alertas.md`, ajustada pelo AJ-01), na ordem:
 * Gravidade · ID · Categoria · Condições · Parâmetro · Score · Data/hora · Ação.
 * Gravidade e Categoria são **colunas próprias** — não um badge só.
 *
 * ⓘ **"Idade", depois do ID, veio do painel de contingência** quando ele foi
 * absorvido por esta tela (2026-09-24). É a idade, nunca a data de nascimento: a
 * tela é projetável, e data de nascimento ao lado do ID é identificador.
 *
 * ⓘ **A nona, "Desfecho", não é da spec** — é do filtro "Resolvidos" que a mesma
 * tela oferece. Fica antes de "Ação" porque é informação do caso, não comando; e
 * fica visível também na fila, vazia, para a tabela não mudar de forma quando a
 * pessoa troca de filtro.
 */
export const Row = styled.div`
  display: grid;
  grid-template-columns: 90px 85px 55px 115px 1fr 200px 50px 100px 100px 260px;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  /* A ultima coluna carrega tres controles (WhatsApp, Tratar, Visualizar) — sem
     largura para ela, "Visualizar" fica fora da area visivel na largura tipica.
     ⚠️ A soma cabe nos ~1245px de conteudo de uma tela de 1440: com Idade e o
     Parametro em tres camadas, 1400px empurrava "Tratar" para fora (medido no
     navegador em 2026-09-25). "Condicoes" e a coluna que cede. */
  min-width: 1240px;
`;

export const HeaderRow = styled(Row)`
  border-bottom: 1px solid #f0f0f0;
  font-size: 12px;
  font-weight: 600;
  color: #828282;
  text-transform: uppercase;
`;

export const BodyRow = styled(Row)`
  border-bottom: 1px solid #f7f7f7;
  font-size: 13px;
  color: #4f4f4f;

  &:last-child {
    border-bottom: none;
  }
`;

export const Badge = styled.span<BadgeProps>`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  color: ${({ color }) => readableTextOn(color)};
  background: ${({ color }) => color};
  white-space: nowrap;
`;

export const PatientLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  color: #2d9cdb;
  font-weight: 600;
  text-decoration: underline;
  cursor: pointer;
`;

export const Muted = styled.span`
  color: #9a9a9a;
`;

export const ParameterCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

export const ParameterLabel = styled.strong`
  font-weight: 600;
  color: #4f4f4f;
`;

/** O trecho da régua que a leitura cruzou — a linha "por que disparou". */
export const Ruler = styled.small`
  font-size: 11px;
  color: #9a9a9a;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const Outcome = styled.span`
  color: #6b6b6b;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const Conditions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  padding-right: 8px;
  color: #6b6b6b;
  font-size: 12px;
  line-height: 15px;

  /* Até duas linhas por condição: numa linha só, a coluna de ~175px cortava o
     diagnóstico antes da parte que o distingue ("Hipertensão essencial (prim…"). */
  > span {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    overflow: hidden;
  }
`;

export const MoreConditions = styled.small`
  align-self: flex-start;
  padding: 0 6px;
  border-radius: 8px;
  background: #f0f0f4;
  color: #6d6d78;
  font-size: 11px;
  font-weight: 600;
`;

export const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
`;

export const WhatsappButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 4px;
  background: #25d366;
  color: #ffffff;
  font-size: 12px;
  font-weight: 600;
  text-decoration: none;

  /* Sem pointer-events: none — ele esconderia o title com o motivo. O clique é
     barrado no WhatsappAction. */
  &[aria-disabled='true'] {
    background: #e0e0e0;
    color: #6b6b6b;
    cursor: not-allowed;
  }
`;

export const TreatButton = styled.button`
  padding: 6px 10px;
  border-radius: 4px;
  border: none;
  background: #2d9cdb;
  color: #ffffff;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;

  /* Vira rótulo quando o alerta já está com alguém — ver comentário na tabela. */
  &[aria-disabled] {
    background: #e0e0e0;
    color: #6b6b6b;
    cursor: default;
  }
`;

export const ViewButton = styled.button`
  background: none;
  border: none;
  color: #2d9cdb;
  font-size: 12px;
  font-weight: 600;
  text-decoration: underline;
  cursor: pointer;
`;

export const SpinnerContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 40px 0;
`;

export const EmptyBox = styled.div`
  display: flex;
  justify-content: center;
  padding: 24px 0;
`;

/**
 * A falha de consulta, **fixa na tela**. Não é toast de propósito: um aviso que some
 * em segundos deixa para trás uma tela igual à de "não há alertas".
 */
export const FailureBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  padding: 14px 18px;
  margin-bottom: 16px;
  border: 1px solid #f1b5b5;
  border-left: 4px solid #c62828;
  border-radius: 6px;
  background: #fdf2f2;
  color: #8e1c1c;
  font-size: 14px;

  strong {
    display: block;
    margin-bottom: 2px;
  }
`;

export const Filters = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;

  select {
    height: 34px;
    border-radius: 4px;
    border: 1px solid #e0e0e0;
    padding: 0 8px;
    color: #4f4f4f;
    background: #ffffff;
  }

  label {
    font-size: 12px;
    color: #828282;
  }
`;
